import { FC, useState } from 'react';
import { AutocompleteInput, Modal } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Stack } from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useDebouncedCallback } from 'use-debounce';
import { useBenefits } from 'store/benefits';

interface Values {
  benefitIds: string[];
}

const INITIAL_VALUES: Values = {
  benefitIds: [],
};

interface Props extends ModalProps {
  disabledBenefits?: string[];
  closeModal: (
    param?: { action: 'CLOSE' } | { action: 'SELECT'; benefitIds: string[] },
  ) => void;
}

export const BenefitsSelectModal: FC<Props> = ({
  closeModal,
  disabledBenefits,
}) => {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const debouncedOnSearch = useDebouncedCallback(setSearch, 300);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  const { data: benefits } = useBenefits({
    name: [search],
  });

  const onSubmit: SubmitHandler<Values> = (values) => {
    closeModal({
      action: 'SELECT',
      benefitIds: values.benefitIds,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('alternatives.addBenefitsDialogTitle')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.close'),
          },
          {
            variant: 'primary',
            label: t('common.confirm'),
            type: 'submit',
            disabled: !isDirty,
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="benefitIds"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <AutocompleteInput
                  label={t('menu.benefits')}
                  name={name}
                  value={value}
                  multiple
                  required
                  onChange={onChange}
                  options={
                    benefits?.items.map((benefit) => ({
                      value: benefit.id,
                      label: benefit.name,
                      disabled: disabledBenefits?.includes(benefit.id),
                    })) ?? []
                  }
                  onInputChange={debouncedOnSearch}
                  ref={ref}
                />
              );
            }}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
